/**
 * How-to videos: the appConfig/helpVideos reader, the YouTube id parser and
 * the role / grade filter. Nothing here touches Google — the parser is pure,
 * the loader reads one Firestore doc, and rendering happens in
 * HelpVideosPanel / HelpVideoPlayer (click-to-play, youtube-nocookie only).
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_HELP_VIDEOS } from './helpVideos.defaults';

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const YT_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com']);

const okId = (id) => (typeof id === 'string' && YT_ID.test(id) ? id : null);

/**
 * Accepts https://www.youtube.com/watch?v=ID (extra params fine),
 * https://youtu.be/ID, https://www.youtube.com/shorts/ID and
 * https://www.youtube.com/embed/ID. http://, other hosts and garbage → null.
 */
export function parseYouTubeId(url) {
  if (typeof url !== 'string') return null;
  let u;
  try {
    u = new URL(url.trim());
  } catch (e) {
    return null;
  }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.toLowerCase();
  const parts = u.pathname.split('/').filter(Boolean);
  if (host === 'youtu.be') return okId(parts[0]);
  if (!YT_HOSTS.has(host)) return null;
  if (parts[0] === 'watch') return okId(u.searchParams.get('v'));
  if ((parts[0] === 'shorts' || parts[0] === 'embed' || parts[0] === 'live' || parts[0] === 'v') && parts[1]) {
    return okId(parts[1]);
  }
  return null;
}

/** Privacy-enhanced player URL: only ever mounted inside the player modal. */
export function nocookieEmbedUrl(id) {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?rel=0&autoplay=1`;
}

/** Fallback link for browsers that block third-party frames. */
export function youtubeWatchUrl(id) {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
}

/**
 * Pinned mapping (owner decision 2026-09-13) from the profile `grade`
 * vocabulary (utils/roleConfig FALLBACK_GRADE_LEVELS) to helpVideos grade
 * tokens. Unknown / Other / null → null = unfiltered.
 */
const GRADE_TOKEN_BY_PROFILE_GRADE = Object.freeze({
  'early childhood': '4K',
  'pre-k': '4K',
  '4k': '4K',
  kindergarten: 'K',
  k: 'K',
  '1st grade': '1',
  '2nd grade': '2',
  '3rd grade': '3',
});

export function normalizeProfileGrade(grade) {
  if (typeof grade !== 'string') return null;
  return GRADE_TOKEN_BY_PROFILE_GRADE[grade.trim().toLowerCase()] || null;
}

/** helpVideos `roles` vocabulary is teacher | tls | admin. */
export function roleTokenFor(primaryRole) {
  switch (primaryRole) {
    case 'teacher':
    case 'ea':
      return 'teacher';
    case 'tls':
      return 'tls';
    case 'admin':
    case 'districtAdmin':
      return 'admin';
    default:
      return null;
  }
}

export function formatDuration(durationSec) {
  if (typeof durationSec !== 'number' || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  if (durationSec < 60) return `${Math.round(durationSec)} sec`;
  return `${Math.max(1, Math.round(durationSec / 60))} min`;
}

/**
 * Filter + sort the raw doc entries for one viewer.
 *  - enabled must be true; source must be 'youtube' (anything else is
 *    skipped and logged — a later 'storage' source is additive);
 *  - url must parse (parsed at render time, never stored);
 *  - roles: [] = all, else must include the viewer's role token;
 *  - grades: applied to TEACHERS only, [] = all; a teacher whose grade is
 *    unknown sees everything (never starve a missing profile field).
 */
export function filterHelpVideos(videos, { roleToken = null, gradeToken = null, applyGrade = false, warn = defaultWarn } = {}) {
  if (!Array.isArray(videos)) return [];
  const kept = [];
  videos.forEach((v, i) => {
    if (!v || typeof v !== 'object') return;
    const label = v.id || `#${i}`;
    if (v.enabled !== true) return;
    if (v.source !== 'youtube') {
      warn(`[helpVideos] skipping "${label}": unsupported source "${String(v.source)}"`);
      return;
    }
    const videoId = parseYouTubeId(v.url);
    if (!videoId) {
      warn(`[helpVideos] skipping "${label}": url is not a recognised https YouTube URL`);
      return;
    }
    if (Array.isArray(v.roles) && v.roles.length > 0 && !v.roles.includes(roleToken)) return;
    if (applyGrade && gradeToken && Array.isArray(v.grades) && v.grades.length > 0 && !v.grades.includes(gradeToken)) return;
    kept.push({ ...v, videoId });
  });
  const order = (v) => (typeof v.order === 'number' && Number.isFinite(v.order) ? v.order : Number.POSITIVE_INFINITY);
  return kept
    .map((v, i) => ({ v, i }))
    .sort((a, b) => order(a.v) - order(b.v) || a.i - b.i)
    .map(({ v }) => v);
}

function defaultWarn(msg) {
  if (typeof console !== 'undefined' && console.warn) console.warn(msg);
}

/**
 * One doc read, on demand (never on app load). Missing doc, bad shape or any
 * error → the in-code defaults (empty in this release).
 */
export async function loadHelpVideos() {
  try {
    const snap = await getDoc(doc(db, 'appConfig', 'helpVideos'));
    if (snap.exists()) {
      const data = snap.data() || {};
      if (Array.isArray(data.videos)) return { videos: data.videos, source: 'firestore' };
    }
  } catch (err) {
    defaultWarn(`[helpVideos] load failed, using defaults: ${err && err.message ? err.message : err}`);
  }
  return { videos: [...DEFAULT_HELP_VIDEOS], source: 'defaults' };
}
